import { forwardRef } from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import { IntakeTransferRequest } from '../../types/intakeTransfer';
import { TRANSFER_STATUS_CHIP, describeTransfer, personName } from './transferUtils';

interface TransferRequestCardProps {
  request: IntakeTransferRequest;
  highlighted?: boolean;
  showStudent?: boolean;
  /** Only passed for a Super Admin, and only for a pending request. */
  onApprove?: () => void;
  onReject?: () => void;
  onOpenStudent?: () => void;
}

export const TransferRequestCard = forwardRef<HTMLDivElement, TransferRequestCardProps>(
  ({ request, highlighted, showStudent, onApprove, onReject, onOpenStudent }, ref) => {
    const chip = TRANSFER_STATUS_CHIP[request.status];
    const { intake, batch } = describeTransfer(request);
    return (
      <Paper
        ref={ref}
        variant="outlined"
        data-testid={`transfer-request-${request.id}`}
        sx={{
          p: 2,
          borderColor: highlighted ? 'primary.main' : undefined,
          borderWidth: highlighted ? 2 : 1,
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {showStudent && (
                <Typography variant="subtitle2">
                  {request.student.firstName} {request.student.lastName} (
                  {request.student.studentNumber})
                </Typography>
              )}
              <Chip size="small" color={chip.color} label={chip.label} />
              <Typography variant="caption" color="text.secondary">
                {request.application.applicationNumber}
                {request.application.university ? ` · ${request.application.university.name}` : ''}
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Intake: {intake}
            </Typography>
            {batch && <Typography variant="body2">Batch: {batch}</Typography>}
            <Typography variant="body2" color="text.secondary">
              Reason: {request.reason}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div">
              Requested by {personName(request.requestedBy)} on{' '}
              {new Date(request.createdAt).toLocaleString()}
            </Typography>
            {request.reviewedBy && request.reviewedAt && (
              <Typography variant="caption" color="text.secondary" component="div">
                {request.status === 'APPROVED' ? 'Approved' : 'Rejected'} by{' '}
                {personName(request.reviewedBy)} on {new Date(request.reviewedAt).toLocaleString()}
                {request.reviewNote ? ` — ${request.reviewNote}` : ''}
              </Typography>
            )}
          </Box>
          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ flexShrink: 0 }}>
            {onOpenStudent && (
              <Button size="small" onClick={onOpenStudent}>
                Student
              </Button>
            )}
            {onApprove && (
              <Button size="small" variant="contained" color="success" onClick={onApprove}>
                Approve
              </Button>
            )}
            {onReject && (
              <Button size="small" variant="outlined" color="error" onClick={onReject}>
                Reject
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
    );
  },
);
TransferRequestCard.displayName = 'TransferRequestCard';
